import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Checkbox, Icon, Button } from 'tea-component';
import { getLocale, useIntl } from 'umi';
import { arrayToSimpleObj } from '@op/utils';
import CardCommon from '@op/components/CardCommon';
import './index.scss';
import { getRealXAxisValue } from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/utils';
import { getEventName } from '@op/pages/Overview/KeyMetrics/utils';
import { formatTimeToShow } from '@op/utils/time';
import { useSyncState } from '@op/hooks/common';
import type { LocaleType } from '@op/types/common';

// Helper to get display name - supports both Opinion (name/name_cn) and Strategic (game_title/event_content) data
function getDisplayName(item: any, lang: LocaleType): string {
  // Try Opinion module fields first
  if (item.name || item.name_cn) {
    return getEventName(item, lang);
  }
  // For Strategic module:
  // - Level 2 items (individual events) have event_content - show that
  // - Level 1 items (category headers) only have name/value - show name
  // Priority: event_content (specific event) > game_title (category)
  return item.event_content || item.game_title || '';
}

// Helper to format date for display - supports both start_time and date fields
function formatEventDate(item: any): string {
  const date = item.start_time || item.date;
  if (!date) return '';
  // If it has start_time/end_time (Opinion format), use formatTimeToShow
  if (item.start_time) {
    return formatTimeToShow({
      ...item,
      date_type: 'hourly',
      hourlyIsSame: true,
    });
  }
  // Otherwise just show the date (Strategic format)
  return date;
}

export default function EventSetting({
  visible,
  close,
  confirm,
  data,
  timeFormat,
  dateType,
  xAxisData,
  autoConfirm = true, // Set to false to prevent auto-confirming on mount (Strategic module doesn't need this)
  onSave, // Optional callback to save selected event IDs to backend (Strategic module)
}: any) {
  const { formatMessage } = useIntl();
  const lang = getLocale();

  const {
    state: selectValue,
    setState: setSelectValue,
    syncStates: syncSelectValue,
    revertToPrevious: revertSelectValue,
    initStates: initSelectValue,
  } = useSyncState<any>({});

  function closeWindow() {
    close();
    revertSelectValue();
  }

  function syncAllStates() {
    syncSelectValue();
  }

  const changeSelectValue = (value: any, key?: string) => {
    if (key) {
      setSelectValue({ ...selectValue, [key]: value });
    } else {
      setSelectValue(value);
    }
  };

  const [levelOneActive, setLevelOneActive] = useState('');
  const [levelOneOptions, setLevelOneOptions] = useState<
    Array<{
      name: string;
      name_cn: string;
      value: string;
      children: Array<string>;
    }>
  >([]);

  // Helper to get the event date - supports both 'start_time' (Opinion module) and 'date' (Strategic module)
  function getEventDate(item: any): string {
    return item.start_time || item.date || '';
  }

  function getOnlyOneKey(item: any) {
    // Use id as primary unique identifier, fallback to composite key for backwards compatibility
    // Strategic module uses: id, game_title, date, event_content
    // Opinion module uses: event_type, name, start_time, url
    if (item.id !== undefined) {
      return `${item.id}$${item.game_title || item.name || ''}$${getEventDate(item)}`;
    }
    return (
      item.event_type + '$' + (item.name || item.game_title || '') + '$' + getEventDate(item) + '$' + (item.url || '')
    );
  }

  function getCallbackEventList(selectValue: Array<string>) {
    const selectedList = Object.values(selectValue).flat(2);
    const list = Object.values(data)
      .flat(3)
      .filter((item) => selectedList.includes(getOnlyOneKey(item)));

    // Sort by date - support both 'start_time' and 'date' fields
    list.sort((a: any, b: any) => {
      const dateA = getEventDate(a);
      const dateB = getEventDate(b);
      return dateB.localeCompare(dateA); // desc order
    });
    const result: { [key: string]: Array<any> } = {};

    list.forEach((item: any) => {
      const eventDate = getEventDate(item);
      let time = getRealXAxisValue({
        xAxis: eventDate,
        dateType,
        format: timeFormat,
        xAxisData,
      });
      if (!result[time]) {
        result[time] = [];
      }
      result[time].push(item);
    });

    return result;
  }

  // 初始化数据
  useEffect(() => {
    const selected: any = {};
    const levelOneOptions = Object.keys(data || {}).map((key) => {
      const list = data[key] || [];

      const item = list[0] || {};
      const children = list.map((item: any) => getOnlyOneKey(item));

      selected[key] = children;

      return {
        name: key,
        name_cn: item.event_type_cn || key,
        value: key,
        children,
      };
    });

    confirm({
      bubbleEvent: getCallbackEventList(selected),
    });

    initSelectValue(selected);
    setLevelOneActive(levelOneOptions?.[0]?.value);
    setLevelOneOptions(levelOneOptions);
  }, [data]);

  // level one select数据
  const { levelOneSelect, levelOneIndeterminate, levelOneIsClear } =
    useMemo(() => {
      const result: any = {
        levelOneSelect: {},
        levelOneIndeterminate: {},
        levelOneIsClear: false,
      };

      for (const key in data) {
        const element = data[key] || [];
        const selected = selectValue[key] || [];

        if (element.length === selected.length) {
          result.levelOneSelect[key] = true;
          result.levelOneIsClear = true;
        } else if (selected.length > 0) {
          result.levelOneIndeterminate[key] = true;
          result.levelOneIsClear = true;
        } else {
          result.levelOneSelect[key] = false;
        }
      }

      return result;
    }, [selectValue, data]);

  const handleLevelOneToggleClearBtn = () => {
    if (levelOneIsClear) {
      return changeSelectValue(
        Object.keys(selectValue).reduce(
          (pre, key) => ({ ...pre, [key]: [] }),
          {},
        ),
      );
    }

    const item = arrayToSimpleObj(levelOneOptions, 'children', 'value');
    changeSelectValue(item);
  };

  // level two select数据
  const levelTwoOptions = useMemo(() => {
    const list = (data?.[levelOneActive] || []).map((item: any) => ({
      ...item,
      value: getOnlyOneKey(item),
    }));
    // Sort by date - support both 'start_time' and 'date' fields
    // Ascending order (oldest first)
    list.sort((a: any, b: any) => {
      const dateA = getEventDate(a);
      const dateB = getEventDate(b);
      return dateA.localeCompare(dateB); // asc order
    });
    return list;
  }, [levelOneActive, data]);

  const { levelTwoSelect, levelTwoIsClear } = useMemo(() => {
    const value = selectValue[levelOneActive] || [];
    return {
      levelTwoSelect: value,
      levelTwoIsClear: value.length > 0,
    };
  }, [selectValue, levelOneActive]);

  const handleLevelTwoToggleClearBtn = () => {
    if (levelTwoIsClear) {
      return changeSelectValue([], levelOneActive);
    }

    const item = levelOneOptions.find(
      (item: any) => item.value === levelOneActive,
    );
    return changeSelectValue(item?.children || [], levelOneActive);
  };



  return (
    <Modal
      className="dbo-modal-event-setting dbs-common-modal"
      visible={visible}
      caption={formatMessage({ id: 'Events Setting' })}
      onClose={() => closeWindow()}
    >
      <Modal.Body>
        <div className="dbo-event-setting dbc-region-select-container">
          <div className="dbo-event-setting-hd">
            <div className="dbo-event-setting-tips">
              When too many events are selected, some may not be fully displayed on the chart due to space constraints.
            </div>
          </div>
          <div className="dbo-event-setting-bd">
            <CardCommon
              contentCard
              empty={levelOneOptions.length === 0}
              emptyHeight={280}
              hasEmptyTitle={false}
              emptyDescText="There are no events scheduled for this period."
            >
              <div className="dbc-region-select-content-container">
                <div className="dbc-region-select-content-wrap">
                  <div className="dbc-region-select-content-title-wrap">
                    <span>Project</span>
                    <span
                      className="dbc-region-select-button"
                      onClick={handleLevelOneToggleClearBtn}
                    >
                      {formatMessage({
                        id: levelOneIsClear ? 'Clear All' : 'Select All',
                      })}
                    </span>
                  </div>
                  <div className="dbc-region-select-item-wrap">
                    {levelOneOptions.map((item) => (
                      <div
                        key={item.value}
                        onClick={() => setLevelOneActive(item.value)}
                        className={`dbc-region-select-checkbox ${levelOneActive === item.value ? 'active' : ''
                          }`}
                      >
                        <Checkbox
                          value={levelOneSelect[item.value]}
                          indeterminate={levelOneIndeterminate[item.value]}
                          onChange={(value) => {
                            setLevelOneActive(item.value);
                            const selected = value ? item.children : [];
                            changeSelectValue(selected, item.value);
                          }}
                        ></Checkbox>
                        <div className="dbc-region-select-checkbox-name-wrap">
                          <span className="dbc-region-select-checkbox-name">
                            {getDisplayName(item, lang)} ({item.children.length})
                          </span>
                          <Icon type="arrowright" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="dbc-region-select-content-wrap dbc-region-select-content-wrap--right">
                  <div className="dbc-region-select-content-title-wrap">
                    <span className="dbc-region-select-content-country-title">
                      {formatMessage({ id: 'Event' })}
                    </span>
                    <span
                      className="dbc-region-select-button"
                      onClick={handleLevelTwoToggleClearBtn}
                    >
                      {formatMessage({
                        id: levelTwoIsClear ? 'Clear All' : 'Select All',
                      })}
                    </span>
                  </div>
                  <div className="dbc-region-select-item-wrap">
                    <Checkbox.Group
                      value={levelTwoSelect}
                      onChange={(value) => {
                        changeSelectValue(value, levelOneActive);
                      }}
                    >
                      {levelTwoOptions.map((item: any) => {
                        return (
                          <Checkbox
                            name={item.value}
                            key={item.value}
                            className="dbc-region-select-checkbox"
                          >
                            <div className="dbc-region-select-checkbox-name-wrap">
                              <span className="dbc-region-select-checkbox-name">
                                {getDisplayName(item, lang)}
                              </span>
                              <span className="dbc-region-select-checkbox-date">
                                {formatEventDate(item)}
                              </span>
                            </div>
                          </Checkbox>
                        );
                      })}
                    </Checkbox.Group>
                  </div>
                </div>
              </div>
            </CardCommon>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          onClick={() => {
            syncAllStates();
            confirm({
              bubbleEvent: getCallbackEventList(selectValue),
            });
          }}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={() => closeWindow()}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
